import { ReachCampaignDetails } from 'api-clients/monolith';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import ExclamationCircleIcon from '@/assets/icons/exclamation-circle.svg';
import { Banner } from '@/components/ui/banner';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { formatShortDate } from '@/utils/date-util';

function getOtherChannelNames(
  reachCampaigns: ReachCampaignDetails[] | undefined,
  selectedPublisher: ReachCampaignDetails | undefined,
): string | null {
  if (!reachCampaigns || !selectedPublisher) return null;

  const names = reachCampaigns
    .filter(
      publisher =>
        publisher.reach_campaign_id !== selectedPublisher.reach_campaign_id &&
        publisher.status !== 'completed',
    )
    .map(publisher => publisher.sourcing_channel.name);

  return Array.from(new Set(names)).join(', ');
}

export function EndDateSyncBanner() {
  const { campaign, selectedPublisher, hookForm, isFetching } =
    useEditCampaignContext();
  const {
    watch,
    formState: { dirtyFields },
  } = hookForm;
  const endAt = watch('end_at');

  const channelNames = useMemo(
    () => getOtherChannelNames(campaign?.reach_campaigns, selectedPublisher),
    [campaign?.reach_campaigns, selectedPublisher],
  );

  const showBanner =
    !isFetching && !!channelNames && dirtyFields.end_at && !!endAt;

  if (!showBanner) return null;

  return (
    <Banner
      color="amber"
      borderLess
      icon={<img src={ExclamationCircleIcon} alt="" />}
      description={
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="{channelNames} end date will also update to {endAt}"
            values={{
              channelNames,
              endAt: formatShortDate(endAt),
            }}
            description="Banner text to notify user that changing one campaign end date will update other linked campaigns"
          />
        </Typography>
      }
    />
  );
}
